import { message } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { integrationApi, supabase } from "~/store/initializeStore";
import { type ManualMeetingForm } from "../salesCrmTypes";
import { salesMeetingBundleSelectFields, type SalesMeetingRow } from "./useSalesMeetingsQuery";

type UseAddMeetingMutationParams = {
  lockedTeamMemberId: string | null;
  onSuccess: (meeting: SalesMeetingRow, linkedBundleId: string | null) => void;
};

export const useAddMeetingMutation = ({ lockedTeamMemberId, onSuccess }: UseAddMeetingMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: ManualMeetingForm) => {
      // Narrow permission scopes pin the target here as well as in the backend.
      const teamMemberId = lockedTeamMemberId ?? values.teamMemberId;

      if (!teamMemberId) {
        throw new Error("Wybierz sprzedawcę");
      }

      // The insert goes through integration-service so the backend can
      // auto-link a matching purchase even if the edit modal is abandoned.
      const { data } = await integrationApi.post<{
        meeting: { id: string };
        linkedBundleId: string | null;
      }>("/calendly/sales-call/create-manual-meeting", {
        teamMemberId,
        clientEmail: values.clientEmail.trim().toLowerCase(),
        clientName: values.clientName?.trim() || null,
        startTime: values.startTime.toISOString(),
        status: values.status,
        notes: values.notes?.trim() || null,
      });

      const { data: row, error } = await supabase
        .from("sales_meeting")
        .select(
          `*, teamMember:teamMemberId(id, firstName, lastName, email, calendlyEmail), customerBundle:customerBundleId(${salesMeetingBundleSelectFields}), matchedCustomerBundle:matchedCustomerBundleId(${salesMeetingBundleSelectFields})`,
        )
        .eq("id", data.meeting.id)
        .single();

      if (error) throw new Error(error.message);

      return {
        meeting: row as unknown as SalesMeetingRow,
        linkedBundleId: data.linkedBundleId ?? null,
      };
    },
    onSuccess: async ({ meeting, linkedBundleId }) => {
      await queryClient.invalidateQueries({ queryKey: ["salesCrmMeetings"] });
      void message.success(
        linkedBundleId
          ? "Spotkanie dodane — sprzedaż podpięta automatycznie"
          : "Spotkanie dodane — możesz podpiąć sprzedaż",
      );
      onSuccess(meeting, linkedBundleId);
    },
    onError: (error) => {
      void message.error(`Nie udało się dodać spotkania: ${(error as Error).message}`);
    },
  });
};
